/**
 * Closed domain vocabularies shared with the backend API. Every value below is a wire value that
 * the backend compares byte for byte, so a value only changes together with the backend.
 */

/** Template field types (`TemplateFieldType.type`). */
export const FIELD_TYPE = Object.freeze({
  TEXT: 'text',
  TEXTAREA: 'textarea',
  IMAGE: 'image',
  NUMBER: 'number',
  CHECKBOX: 'checkbox',
  DOCUMENT: 'document',
  TABLE: 'table',
  SELECT: 'select',
});

/** @typedef {typeof FIELD_TYPE[keyof typeof FIELD_TYPE]} FieldType */

/** Every field type, in the order the field type picker lists them. */
export const FIELD_TYPES = Object.freeze(Object.values(FIELD_TYPE));

/** Free-text field types; only they take `recommendedLength`. */
export const TEXT_FIELD_TYPES = Object.freeze([FIELD_TYPE.TEXT, FIELD_TYPE.TEXTAREA]);

/** Column types of a table field (`TableColumnType.type`). */
export const TABLE_COLUMN_TYPE = Object.freeze({
  TEXT: 'text',
  NUMBER: 'number',
  SELECT: 'select',
  DOCUMENT: 'document',
});

/** @typedef {typeof TABLE_COLUMN_TYPE[keyof typeof TABLE_COLUMN_TYPE]} ColumnType */

/** Every table column type, in the order the column type picker lists them. */
export const TABLE_COLUMN_TYPES = Object.freeze(Object.values(TABLE_COLUMN_TYPE));

/** Template render modes. */
export const RENDER_MODE = Object.freeze({
  PAGE: 'PAGE',
  INDEX_FRAGMENT: 'INDEX_FRAGMENT',
  INTERNAL: 'INTERNAL',
});

/** @typedef {typeof RENDER_MODE[keyof typeof RENDER_MODE]} RenderMode */

/** Mode applied when a template request carries none. */
export const DEFAULT_RENDER_MODE = RENDER_MODE.PAGE;

/**
 * Editor presentations of a layout context. `GRID` is the admin UI default and is never sent:
 * the backend reads an absent presentation as grid placement. Only `CAROUSEL` goes on the wire.
 */
export const LAYOUT_PRESENTATION = Object.freeze({
  GRID: 'grid',
  CAROUSEL: 'carousel',
});

/** @typedef {typeof LAYOUT_PRESENTATION[keyof typeof LAYOUT_PRESENTATION]} LayoutPresentation */

/** Layout context whose grid fields use the bare base keys (`row`, not `homepage_row`). */
export const DEFAULT_LAYOUT_CONTEXT = 'homepage';

/** Base keys of the grid position fields of a layout context. */
export const GRID_FIELD = Object.freeze({
  ROW: 'row',
  COLUMN: 'column',
  WIDTH: 'width',
  HEIGHT: 'height',
});

/** The four grid position base keys, in declaration order. */
export const GRID_FIELD_BASES = Object.freeze(Object.values(GRID_FIELD));

/**
 * Key of one grid position field of a layout context: the bare base key for the default context,
 * `${context}_${base}` for every other context.
 *
 * @param {string} context
 * @param {typeof GRID_FIELD[keyof typeof GRID_FIELD]} base
 * @returns {string}
 */
export function gridFieldKey(context, base) {
  return context === DEFAULT_LAYOUT_CONTEXT ? base : `${context}_${base}`;
}

/** Checkbox field that pins a page to its saved grid position. */
export const PIN_POSITION_FIELD_KEY = 'pin_position';

/** Text field the backend slugifies into the page filename. */
export const SLUG_FIELD_KEY = 'slug';

/** Suffix of the alt text key the page editor derives from an image field key. */
export const ALT_TEXT_KEY_SUFFIX = '_alt';

/** Suffix of the image position key the page editor derives from an image field key. */
export const POSITION_KEY_SUFFIX = '_position';

/** Render list the backend resolves from the section composition. */
export const PAGE_SECTIONS_KEY = 'page_sections';

/** Page data object holding the section composition choices. */
export const COMPOSITION_DATA_KEY = 'wb_composition';

/** Keys the backend injects into every render context; a template field may not use them. */
export const RESERVED_AUTO_INJECTED_TEMPLATE_FIELD_KEYS = Object.freeze([
  'canonical_url',
  'path_prefix',
  'breadcrumbs',
  'breadcrumb_json_ld',
  'current_page',
  PAGE_SECTIONS_KEY,
  COMPOSITION_DATA_KEY,
  'is_preview',
]);

/** Billing item statuses (`BillingStatus`), computed by the backend from the item's `paidUntil`. */
export const BILLING_STATUS = Object.freeze({
  OVERDUE: 'overdue',
  DUE_SOON: 'due_soon',
  OK: 'ok',
  INACTIVE: 'inactive',
});

/** @typedef {typeof BILLING_STATUS[keyof typeof BILLING_STATUS]} BillingStatus */

/** Billing report display choices. */
export const BILLING_GROUP = Object.freeze({SUPPLIER: 'supplier', CLIENT: 'client'});
export const BILLING_CHART_MODE = Object.freeze({NET: 'net', INCOME_COSTS: 'income-costs'});
export const BILLING_VIEW = Object.freeze({RESULTS: 'results', TO_INVOICE: 'to-invoice'});
